/**
 * Lógica pura de Conversaciones (spec de conversaciones §15.5). Sin React y sin fetch: todo se prueba en
 * `conversaciones.test.ts`.
 *
 * Reglas que valen para todo el archivo:
 * - La ventana de 24 h se calcula SIEMPRE contra `contestarHasta` (el backend corta 60 s antes) y con el
 *   reloj corregido por el desfase con el servidor: con `+24 h` y el reloj de la PC, la caja decía «hasta
 *   14:32» en el último minuto y el envío volvía rechazado.
 * - Las fechas se muestran en la zona del negocio (`TZ_NEGOCIO`), no en la del navegador: un operador de
 *   viaje ve las mismas horas que el local.
 * - Nunca se escriben tarifas acá: los costos salen de lo que manda el backend.
 */

import type {
  CostoRespuesta, EstadoConversacion, FiltroConversaciones, ItemHilo,
} from "../types.conversaciones";
import { TZ_NEGOCIO, diaEnZona } from "./estadisticas";
import { usdPorMensaje } from "./formato";

export const R_CONVERSACIONES = "/activa/oficial/conversaciones";
/** El tope del backend (`MAX_TEXTO_RESPUESTA`): la caja lo cuenta y no deja mandar por encima. */
export const MAX_TEXTO_RESPUESTA = 1600;
/** Por debajo de esto la caja avisa en ámbar cuántos minutos quedan. */
export const FALTA_POCO_MS = 15 * 60_000;

/**
 * Desde `xl` (1280 px) la bandeja va lado a lado: el hilo tiene su propio scroll, la caja no va fija abajo y su
 * detalle (ventana, costo, links) se ve entero (salvo en una pantalla de poco alto, ver `esPantallaBaja`). La página
 * de la conversación y la caja miran la MISMA media query.
 */
export function esComputadora(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(min-width: 1280px)").matches;
}

/**
 * Menos de 800 px de alto: una notebook de 1280x720, o una de 1366x768 con la barra del navegador. Ahí la ficha, el
 * hilo y la caja comparten una columna del alto de la pantalla, y con el detalle de la caja desplegado al hilo le
 * quedaba el mínimo (128 px): la caja lo pliega como en el celular.
 */
export function esPantallaBaja(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-height: 799px)").matches;
}

/**
 * ¿Hay un teclado de computadora a mano? El aviso «Ctrl + Enter para enviar» salía también en el celular, donde no
 * hay Ctrl (lo vio la recorrida). Se muestra con un puntero fino (mouse o trackpad) en una pantalla de al menos 768 px;
 * en el celular o la tablet, no. Sin `matchMedia` (un navegador viejo) tampoco: un aviso de menos no rompe nada, y el
 * atajo sigue andando igual.
 */
export const MEDIA_TECLADO_FISICO = "(min-width: 768px) and (pointer: fine)";
export function conTecladoFisico(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(MEDIA_TECLADO_FISICO).matches;
}

/** Lo emite quien cambia una conversación (contestar, resolver, baja…) y lo escuchan la lista y el badge. */
export const EVENTO_CONVERSACIONES_CAMBIO = "wa-oficial:conversaciones-cambio";

/**
 * Avisa que algo cambió: la lista y el contador de la pestaña se vuelven a pedir en el momento. Sin esto
 * la fila seguía diciendo «Sin responder» hasta 20 s con el hilo ya en «Respondida».
 */
export function avisarCambioConversaciones(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(EVENTO_CONVERSACIONES_CAMBIO));
}

/**
 * La clave de idempotencia de un envío (`^[A-Za-z0-9-]{8,64}$` en el backend). Nunca puede salir
 * constante: con la misma clave el backend devuelve el primer envío como `repetido` y el segundo mensaje
 * no sale, sin aviso.
 */
export function nuevaClaveCliente(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(b);
  else for (let i = 0; i < b.length; i++) b[i] = Math.floor(Math.random() * 256);
  return `${Date.now().toString(36)}-${Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("")}`;
}

/** Los 6 filtros, «Por contestar» primero: es el de por defecto y el mismo conjunto que el badge. */
export const FILTROS: { clave: FiltroConversaciones; label: string }[] = [
  { clave: "por_contestar", label: "Por contestar" },
  { clave: "sin_responder", label: "Sin responder (todas)" },
  { clave: "ventana_abierta", label: "Ventana abierta" },
  { clave: "todas", label: "Todas" },
  { clave: "resueltas", label: "Resueltas" },
  { clave: "bajas", label: "Bajas" },
];

const esFiltro = (x: string | null): x is FiltroConversaciones => FILTROS.some((f) => f.clave === x);

/** Los filtros de la lista que viven en la URL. Un filtro desconocido cae al de por defecto. */
export function filtroDeUrl(p: URLSearchParams): { filtro: FiltroConversaciones; campana: string; q: string; page: number } {
  const f = p.get("filtro");
  return {
    filtro: esFiltro(f) ? f : "por_contestar",
    campana: p.get("campana") || "",
    q: p.get("q") || "",
    page: Math.max(1, Number.parseInt(p.get("page") || "1", 10) || 1),
  };
}

const CLAVES_LISTA = ["filtro", "campana", "q", "page"] as const;

/**
 * Sólo filtro, campana, q y page: nunca `accion` ni otra cosa. Copiar `location.search` entero pasaba
 * `?accion=reabrir` a la conversación siguiente y le abría la reapertura (una plantilla paga) a otra persona.
 */
export function paramsDeLista(p: URLSearchParams): URLSearchParams {
  const n = new URLSearchParams();
  for (const k of CLAVES_LISTA) {
    const v = p.get(k);
    if (v) n.set(k, v);
  }
  return n;
}

/** El link a una conversación conservando los filtros de la lista (y nada más). */
export function hrefConversacion(id: string, p: URLSearchParams): string {
  const s = paramsDeLista(p).toString();
  return `${R_CONVERSACIONES}/${encodeURIComponent(id)}${s ? `?${s}` : ""}`;
}

/** El link a la lista con sus filtros («← Conversaciones»). */
export function hrefLista(p: URLSearchParams): string {
  const s = paramsDeLista(p).toString();
  return `${R_CONVERSACIONES}${s ? `?${s}` : ""}`;
}

export const ESTADO_CONVERSACION: Record<EstadoConversacion, { texto: string; cls: string }> = {
  sin_responder: { texto: "Sin responder", cls: "bg-amber-50 text-amber-700" },
  respondida: { texto: "Respondida", cls: "bg-emerald-50 text-emerald-700" },
  resuelta: { texto: "Resuelta", cls: "bg-slate-100 text-slate-600" },
  baja: { texto: "Baja", cls: "bg-red-50 text-red-700" },
};

/**
 * El estado de una respuesta manual, dicho para el operador. `desconocido` pide que no la mande de nuevo:
 * Twilio pudo haberla aceptado, y un segundo envío le llegaría dos veces.
 */
export const TEXTO_ESTADO_ENVIO: Record<string, string> = {
  reservado: "Saliendo…",
  aceptado: "Saliendo…",
  enviado: "Enviado",
  entregado: "Entregado",
  leido: "Leído",
  fallido: "No salió",
  no_entregado: "No se entregó",
  desconocido: "Confirmando con WhatsApp… No la mandes de nuevo.",
  sin_confirmacion: "WhatsApp no confirmó si llegó. Se cobró igual.",
};

/** Estados de un envío manual que todavía no se confirmaron: el refresco va más rápido y la caja avisa. */
export const ESTADOS_EN_CURSO: ReadonlySet<string> = new Set(["reservado", "aceptado", "desconocido"]);

/** «hace un momento» → «Hace un momento». `haceCuanto` ya dice «hace…»: sin esto se leía «Hace hace…». */
export function capitalizar(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Cuánto está corrido el reloj de la PC contra el del servidor. Una fecha que no se entiende no corre nada. */
export function desfaseMs(ahoraServidor: string, localMs: number): number {
  const t = Date.parse(ahoraServidor);
  return Number.isFinite(t) ? t - localMs : 0;
}

const FMT_HORA = new Intl.DateTimeFormat("es-AR", { timeZone: TZ_NEGOCIO, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const FMT_DIA_SEMANA = new Intl.DateTimeFormat("es-AR", { timeZone: TZ_NEGOCIO, weekday: "long" });

const fechaValida = (iso: string): Date | null => {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
};

/** «14:32» en la zona del negocio. Una fecha que no se entiende no dice nada. */
export function horaNegocio(iso: string): string {
  const d = fechaValida(iso);
  return d ? FMT_HORA.format(d) : "";
}

/** «22/09» a partir de un «2026-09-22». */
const ddmm = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/** «22/09/2026» en la zona del negocio (la baja «desde el …»). */
export function fechaNegocio(iso: string | null | undefined): string {
  const d = iso ? fechaValida(iso) : null;
  if (!d) return "";
  const dia = diaEnZona(d);
  return `${ddmm(dia)}/${dia.slice(0, 4)}`;
}

/** Días de calendario (en la zona del negocio) entre `ahora` y `d`: 0 hoy, 1 mañana, -1 ayer. */
function diasDesdeHoy(d: Date, ahora: Date): number {
  return Math.round((Date.parse(`${diaEnZona(d)}T00:00:00Z`) - Date.parse(`${diaEnZona(ahora)}T00:00:00Z`)) / 86_400_000);
}

/** Para el chip de la lista: «14:32», «mañana 09:05» o «25/09 14:32». */
export function textoVentanaCorto(contestarHasta: string, ahora: Date): string {
  const d = fechaValida(contestarHasta);
  if (!d) return "";
  const dias = diasDesdeHoy(d, ahora);
  if (dias === 0) return FMT_HORA.format(d);
  if (dias === 1) return `mañana ${FMT_HORA.format(d)}`;
  return `${ddmm(diaEnZona(d))} ${FMT_HORA.format(d)}`;
}

/** Para la caja: «hoy a las 14:32», «mañana a las 09:05» o «el 25/09 a las 14:32». */
export function textoVentanaLargo(contestarHasta: string, ahora: Date): string {
  const d = fechaValida(contestarHasta);
  if (!d) return "";
  const dias = diasDesdeHoy(d, ahora);
  if (dias === 0) return `hoy a las ${FMT_HORA.format(d)}`;
  if (dias === 1) return `mañana a las ${FMT_HORA.format(d)}`;
  return `el ${ddmm(diaEnZona(d))} a las ${FMT_HORA.format(d)}`;
}

const msHasta = (contestarHasta: string, ahora: Date): number => {
  const d = fechaValida(contestarHasta);
  return d ? d.getTime() - ahora.getTime() : 0;
};

/** «3 h 20 min», «45 min», «menos de 1 min». Con la ventana cerrada, «0 min». */
export function textoRestante(contestarHasta: string, ahora: Date): string {
  const ms = msHasta(contestarHasta, ahora);
  if (ms <= 0) return "0 min";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "menos de 1 min";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Menos de 15 min para que se cierre (y todavía abierta). */
export function faltaPoco(contestarHasta: string, ahora: Date): boolean {
  const ms = msHasta(contestarHasta, ahora);
  return ms > 0 && ms < FALTA_POCO_MS;
}

/** La línea ámbar de los últimos minutos. Redondea para abajo: nunca promete más tiempo del que queda. */
export function textoQuedanMinutos(contestarHasta: string, ahora: Date): string {
  const min = Math.max(1, Math.floor(msHasta(contestarHasta, ahora) / 60_000));
  return `Quedan ${min} min para contestar con texto libre.`;
}

/** La fecha con la que se ORDENA un ítem (la de su `clave`), que en la automática no es su `at`. */
function fechaDeOrden(i: ItemHilo): Date {
  const iso = String(i.clave || "").split("|")[0];
  return fechaValida(iso) ?? fechaValida(i.at) ?? new Date(0);
}

/**
 * Los separadores por día del hilo («Hoy», «Ayer», «lunes 22/09»), en la zona del negocio. Recibe los
 * ítems YA ordenados y agrupa los consecutivos: el día sale de la misma fecha que ordena, así un grupo
 * nunca queda partido en dos.
 */
export function agruparPorDia(items: ItemHilo[], ahora: Date): { dia: string; titulo: string; items: ItemHilo[] }[] {
  const grupos: { dia: string; titulo: string; items: ItemHilo[] }[] = [];
  for (const i of items) {
    const d = fechaDeOrden(i);
    const dia = diaEnZona(d);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.dia === dia) { ultimo.items.push(i); continue; }
    const dias = diasDesdeHoy(d, ahora);
    const titulo = dias === 0 ? "Hoy" : dias === -1 ? "Ayer" : `${FMT_DIA_SEMANA.format(d)} ${ddmm(dia)}`;
    grupos.push({ dia, titulo, items: [i] });
  }
  return grupos;
}

/**
 * Suma una página al hilo cargado: reemplaza los que ya están (un estado nuevo, un costo) y agrega los
 * nuevos, SIN sacar nada. Reemplazar la lista entera con la página más nueva borraba a los 20 s los
 * mensajes anteriores que el operador había cargado para leer.
 */
export function fusionarItems(actual: Map<string, ItemHilo>, nuevos: ItemHilo[]): Map<string, ItemHilo> {
  const m = new Map(actual);
  for (const i of nuevos) if (i && i.id) m.set(i.id, i);
  return m;
}

/** Por `clave` ascendente (fecha de orden y, en un empate, el id): el mismo orden que pagina el backend. */
export function ordenarItems(m: Map<string, ItemHilo>): ItemHilo[] {
  return Array.from(m.values()).sort((a, b) => (a.clave < b.clave ? -1 : a.clave > b.clave ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** El último envío manual del hilo todavía no se confirmó (reservado, aceptado o desconocido). */
export function ultimoManualEnCurso(items: ItemHilo[]): boolean {
  for (let k = items.length - 1; k >= 0; k--) {
    const i = items[k];
    if (i.tipo === "manual") return ESTADOS_EN_CURSO.has(i.estado);
  }
  return false;
}

/**
 * Lo que es gratis dentro del cupo de servicio: el cargo de Meta, no el envío. Decir «mensajes de servicio gratis» al
 * lado de «Cuesta USD 0,0060» se leía como una contradicción (¿gratis o cuesta?).
 */
export const TEXTO_CUPO_SIN_CARGO = "entró en los mensajes de servicio sin cargo de Meta del mes";

/** La línea del costo de la caja, con lo que manda el backend. El total con separador de miles: «1.000». */
export function textoCosto(c: CostoRespuesta): string {
  if (!c.cupo || c.costoSiGratisUSD === c.costoSinCupoUSD) return `Cada respuesta cuesta ${usdPorMensaje(c.reservaUSD)}.`;
  const total = c.cupo.total.toLocaleString("es-AR");
  if (c.cupo.usados < c.cupo.total) {
    return `Cuesta ${usdPorMensaje(c.costoSiGratisUSD)}, sólo el envío: Meta no la cobra porque entra en los ${total} mensajes de servicio sin cargo de Meta del mes (van ${c.cupo.usados.toLocaleString("es-AR")}). Se reserva ${usdPorMensaje(c.reservaUSD)} y se devuelve la diferencia cuando se entrega.`;
  }
  return `Cuesta ${usdPorMensaje(c.costoSinCupoUSD)}, el envío y el cargo de Meta: este mes ya se usaron los ${total} mensajes de servicio sin cargo de Meta del número.`;
}

/**
 * La línea del costo en el celular, con el detalle plegado. Lo que cuesta ESTA respuesta queda siempre a la vista
 * (la plata no se esconde detrás de un toque); el cupo y la reserva van en «Ver ventana y costo». Sin cupo, la línea
 * de siempre ya es corta.
 */
export function textoCostoCorto(c: CostoRespuesta): string {
  if (!c.cupo || c.costoSiGratisUSD === c.costoSinCupoUSD) return textoCosto(c);
  return `Cuesta ${usdPorMensaje(c.cupo.usados < c.cupo.total ? c.costoSiGratisUSD : c.costoSinCupoUSD)}.`;
}

/** Una respuesta rápida: con la caja vacía la reemplaza; con texto, va en una línea nueva al final. */
export function insertarRapida(actual: string, texto: string): string {
  return actual.trim() === "" ? texto : `${actual}\n${texto}`;
}

/** Cuántos caracteres sobran por encima del tope (0 si entra). */
export function excedente(texto: string): number {
  return Math.max(0, texto.length - MAX_TEXTO_RESPUESTA);
}

/** Saca los escapes de un valor de vCard (`\,` `\;` `\\` `\n`). */
const desescapar = (s: string) => s.replace(/\\([nN,;\\])/g, (_m, c: string) => (c === "n" || c === "N" ? " " : c)).trim();

/**
 * Lee el nombre (FN, o N si no hay FN) y los teléfonos (TEL) de un contacto compartido. El número que
 * pasó la persona se muestra entero en el hilo: no queda escondido detrás de un archivo (R3). Sin
 * BEGIN:VCARD, o sin nombre ni teléfono, null (la burbuja dice «Contacto compartido»).
 */
export function parsearVcard(texto: string): { nombre: string; telefonos: string[] } | null {
  const s = String(texto || "");
  if (!/BEGIN:VCARD/i.test(s)) return null;
  // Las líneas largas vienen partidas: un salto seguido de espacio o tab continúa la anterior.
  const lineas = s.replace(/\r?\n[ \t]/g, "").split(/\r?\n/);
  let nombre = "";
  let n = "";
  const telefonos: string[] = [];
  for (const l of lineas) {
    const dos = l.indexOf(":");
    if (dos < 0) continue;
    // «item1.TEL;type=CELL;waid=…» → TEL
    const prop = l.slice(0, dos).split(";")[0].replace(/^[^.]*\./, "").trim().toUpperCase();
    const crudo = l.slice(dos + 1);
    if (prop === "FN" && !nombre) nombre = desescapar(crudo);
    else if (prop === "N" && !n) {
      // Se parte por los «;» que no están escapados (sin lookbehind: Safari viejo no lo entiende y
      // rompería el módulo entero al cargarlo).
      const [apellido = "", nombrePila = ""] = crudo.replace(/\\;/g, "").split(";").map((x) => desescapar(x.replace(//g, "\\;")));
      n = [nombrePila, apellido].filter(Boolean).join(" ");
    } else if (prop === "TEL") {
      const t = desescapar(crudo).replace(/^tel:/i, "").trim();
      if (t) telefonos.push(t);
    }
  }
  if (!nombre) nombre = n;
  if (!nombre && !telefonos.length) return null;
  return { nombre, telefonos };
}
